import { useState } from "react";
import {
  Cobro,
  Caso,
  Holding,
  Usuario,
  EventoEstado,
  Documento,
} from "../../types";
import { Badge, getEstadoCobroBadgeVariant } from "../common/Badge";
import { CobroDetail } from "./CobroDetail";

interface CobroViewProps {
  cobros: Cobro[];
  casos: Caso[];
  holdings: Holding[];
  usuarios: Usuario[];
  eventosEstado: EventoEstado[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onCobroUpdate: (cobro: Cobro) => void;
  onEventoEstadoCreate: (data: Omit<EventoEstado, "id">) => number;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
}

const ESTADO_COBRO_LABELS: Record<string, string> = {
  pendiente_certificado: "Pend. Certificado",
  certificado_obtenido: "Certificado Obtenido",
  facturado: "Facturado",
  cobrado: "Cobrado",
  incobrable: "Incobrable",
};

export function CobroView({
  cobros,
  casos,
  holdings,
  usuarios,
  eventosEstado,
  documentos,
  usuarioActual,
  onCobroUpdate,
  onEventoEstadoCreate,
  onDocumentoCreate,
}: CobroViewProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>("todos");

  const filtered = filtroEstado === "todos"
    ? cobros
    : cobros.filter((c) => c.estado === filtroEstado);

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime()
  );

  const selected = cobros.find((c) => c.id === selectedId);
  const getHoldingNombre = (holdingId: number) =>
    holdings.find((h) => h.id === holdingId)?.nombre || "-";
  const getCasoTitulo = (solicitudId: number) =>
    casos.find((c) => c.id === solicitudId)?.titulo || "-";

  const formatMoney = (value?: number) =>
    value ? `$${value.toLocaleString("es-CL")}` : "-";

  return (
    <div className="flex h-full">
      {/* Left panel - list */}
      <div className="w-96 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Cobro</h2>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="mt-2 w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="todos">Todos los estados</option>
            <option value="pendiente_certificado">Pend. Certificado</option>
            <option value="certificado_obtenido">Certificado Obtenido</option>
            <option value="facturado">Facturado</option>
            <option value="cobrado">Cobrado</option>
            <option value="incobrable">Incobrable</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">
              No hay cobros
            </div>
          ) : (
            sorted.map((cobro) => (
              <div
                key={cobro.id}
                onClick={() => setSelectedId(cobro.id)}
                className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${
                  selectedId === cobro.id ? "bg-blue-50 border-l-2 border-l-blue-600" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-slate-800 truncate">
                    {getCasoTitulo(cobro.solicitudId)}
                  </span>
                  <Badge variant={getEstadoCobroBadgeVariant(cobro.estado)}>
                    {ESTADO_COBRO_LABELS[cobro.estado] || cobro.estado}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  {getHoldingNombre(cobro.holdingId)}
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-400">
                    {new Date(cobro.fechaCreacion).toLocaleDateString("es-CL")}
                  </span>
                  <span className="text-xs font-medium text-slate-600">
                    {formatMoney(cobro.montoCobro)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right panel - detail */}
      <div className="flex-1 overflow-hidden">
        {selected ? (
          <CobroDetail
            cobro={selected}
            caso={casos.find((c) => c.id === selected.solicitudId)}
            holding={holdings.find((h) => h.id === selected.holdingId)}
            usuarios={usuarios}
            eventosEstado={eventosEstado.filter(
              (e) => e.entidadTipo === "cobro" && e.entidadId === selected.id
            )}
            documentos={documentos}
            usuarioActual={usuarioActual}
            onCobroUpdate={onCobroUpdate}
            onEventoEstadoCreate={onEventoEstadoCreate}
            onDocumentoCreate={onDocumentoCreate}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-slate-400">
            <p>Selecciona un cobro para ver su detalle</p>
          </div>
        )}
      </div>
    </div>
  );
}
